/**
 * Contact Section
 * Использует переиспользуемые компоненты
 * Примечание: использует useState для формы в редакторе
 */
import React, { useState } from 'react';
import { Text, Icon, Container } from '../components';
import Input from '../../components/ui/Input';

// Компонент Яндекс Карты
function YandexMap({ coords, zoom = 15, height = 300 }) {
  if (!coords) return null;

  const [lat, lng] = coords.split(',').map(c => c.trim());
  if (!lat || !lng) return null;

  const mapUrl = `https://yandex.ru/map-widget/v1/?ll=${lng},${lat}&z=${zoom}&pt=${lng},${lat},pm2rdm`;

  return (
    <div className="rounded-xl overflow-hidden border border-border">
      <iframe
        src={mapUrl}
        width="100%"
        height={height}
        frameBorder="0"
        allowFullScreen
        style={{ display: 'block' }}
        title="Яндекс Карта"
      />
    </div>
  );
}

export default function ContactSection({ data, styles }) {
  const contacts = data?.contacts || [];
  const socials = data?.socials || [];
  const sectionStyle = data?.style || 'split';
  const showForm = data?.showForm !== false;
  const showMap = data?.showMap === true;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Форма отправлена! (демо)');
    setFormData({ name: '', email: '', message: '' });
  };

  const ContactInfo = () => (
    <div className="space-y-4 md:space-y-6">
      {contacts.map((contact, index) => (
        <div key={contact.id || index} className="flex items-start gap-4">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Icon name={contact.icon || 'Info'} size="md" />
          </div>
          <div>
            <Text
              as="p"
              defaults={{ size: 'sm', weight: 'normal', color: 'text-secondary' }}
              className="mb-0.5"
            >
              {contact.label}
            </Text>
            {contact.link ? (
              <a
                href={contact.link}
                className="text-base md:text-lg font-medium hover:text-primary transition-colors"
              >
                {contact.value}
              </a>
            ) : (
              <Text
                as="p"
                defaults={{ size: 'lg', weight: 'medium', color: 'foreground' }}
              >
                {contact.value}
              </Text>
            )}
          </div>
        </div>
      ))}

      {socials.length > 0 && (
        <div className="pt-4 md:pt-6 border-t border-border">
          <Text
            as="p"
            defaults={{ size: 'sm', weight: 'normal', color: 'text-secondary' }}
            className="mb-3"
          >
            Мы в соцсетях
          </Text>
          <div className="flex gap-2">
            {socials.map((social, index) => (
              <a
                key={social.id || index}
                href={social.link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-colors"
              >
                <Icon name={social.icon || 'Link'} size="sm" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const ContactForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {data?.formTitle && (
        <Text
          as="h3"
          data={typeof data.formTitle === 'object' ? data.formTitle : { text: data.formTitle }}
          defaults={{ size: 'xl', weight: 'semibold', color: 'foreground' }}
          className="mb-4"
        />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          placeholder="Ваше имя"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
        <Input
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
      </div>
      <textarea
        placeholder="Ваше сообщение"
        value={formData.message}
        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
        rows={4}
        required
        className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none text-base md:text-sm"
      />
      <button
        type="submit"
        className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
      >
        <Icon name="Send" size="sm" />
        Отправить
      </button>
    </form>
  );

  const renderSplitStyle = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
      <div>
        <ContactInfo />
      </div>
      <div className="space-y-6">
        {showMap && (
          <YandexMap
            coords={data?.mapCoords}
            zoom={data?.mapZoom}
            height={data?.mapHeight}
          />
        )}
        {showForm && (
          <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
            <ContactForm />
          </div>
        )}
      </div>
    </div>
  );

  const renderCenteredStyle = () => (
    <div className="max-w-4xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
        {contacts.map((contact, index) => (
          <div key={contact.id || index} className="text-center">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-3">
              <Icon name={contact.icon || 'Info'} size={22} />
            </div>
            <p className="text-xs md:text-sm text-text-secondary mb-1">{contact.label}</p>
            {contact.link ? (
              <a
                href={contact.link}
                className="text-sm md:text-base font-medium hover:text-primary transition-colors block truncate"
              >
                {contact.value}
              </a>
            ) : (
              <p className="text-sm md:text-base font-medium truncate">{contact.value}</p>
            )}
          </div>
        ))}
      </div>

      {socials.length > 0 && (
        <div className="flex justify-center gap-3 mb-8 md:mb-12">
          {socials.map((social, index) => (
            <a
              key={social.id || index}
              href={social.link || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-colors"
            >
              <Icon name={social.icon || 'Link'} size={20} />
            </a>
          ))}
        </div>
      )}

      {showMap && (
        <div className="mb-8">
          <YandexMap
            coords={data?.mapCoords}
            zoom={data?.mapZoom}
            height={data?.mapHeight}
          />
        </div>
      )}

      {showForm && (
        <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl p-6 md:p-8">
          <ContactForm />
        </div>
      )}
    </div>
  );

  const renderMinimalStyle = () => (
    <div className="max-w-2xl mx-auto text-center">
      <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12 mb-8">
        {contacts.slice(0, 3).map((contact, index) => (
          <div key={contact.id || index}>
            {contact.link ? (
              <a
                href={contact.link}
                className="flex items-center gap-2 hover:text-primary transition-colors"
              >
                <Icon name={contact.icon || 'Info'} size={18} className="text-primary" />
                <span className="text-sm md:text-base">{contact.value}</span>
              </a>
            ) : (
              <div className="flex items-center gap-2">
                <Icon name={contact.icon || 'Info'} size={18} className="text-primary" />
                <span className="text-sm md:text-base">{contact.value}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {socials.length > 0 && (
        <div className="flex justify-center gap-3 mb-8">
          {socials.map((social, index) => (
            <a
              key={social.id || index}
              href={social.link || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-colors"
            >
              <Icon name={social.icon || 'Link'} size={16} />
            </a>
          ))}
        </div>
      )}

      {showMap && (
        <div className="mb-8">
          <YandexMap
            coords={data?.mapCoords}
            zoom={data?.mapZoom}
            height={data?.mapHeight}
          />
        </div>
      )}

      {showForm && <ContactForm />}
    </div>
  );

  return (
    <section
      style={{
        paddingTop: styles?.paddingTop || 64,
        paddingBottom: styles?.paddingBottom || 64,
        backgroundColor: styles?.backgroundColor || '#FFFFFF'
      }}
    >
      <Container>
        {/* Заголовок */}
        <Text
          as="h2"
          data={typeof data?.title === 'object' ? data.title : { text: data?.title }}
          defaults={{ size: '3xl', weight: 'bold', color: 'foreground' }}
          align="center"
          className={data?.subtitle ? 'mb-2 md:mb-3' : 'mb-8 md:mb-12'}
        />

        {/* Подзаголовок */}
        <Text
          as="p"
          data={typeof data?.subtitle === 'object' ? data.subtitle : { text: data?.subtitle }}
          defaults={{ size: 'lg', weight: 'normal', color: 'text-secondary' }}
          align="center"
          className="mb-8 md:mb-12"
        />

        {sectionStyle === 'split' && renderSplitStyle()}
        {sectionStyle === 'centered' && renderCenteredStyle()}
        {sectionStyle === 'minimal' && renderMinimalStyle()}
      </Container>
    </section>
  );
}
